/**
 * <CardImage /> — the canonical way to render a card thumbnail anywhere
 * in the app. Centralises:
 *
 *   • URL derivation (always `/api/assets/cards/{cardId}/{variant}`)
 *   • Cache-busting via a `bust` prop (admin Card Editor sets this after
 *     upload so the user sees the fresh image without a hard reload)
 *   • Graceful fallback to a coloured monogram when the card has no image
 *   • Aspect-ratio preservation (ID-1 = 86 × 54mm ≈ 1.586:1)
 *
 * Variants:
 *   thumb     — 256px wide PNG       (use in lists)
 *   preview   — 800px wide WebP      (use in detail / editor)
 *   processed — full-size PNG        (use when the user explicitly enlarges)
 *
 * Sizes:
 *   xs (h-6) sm (h-8) md (h-10) lg (h-14) xl (h-20) 2xl (h-28)
 */
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

const BASE = process.env.REACT_APP_BACKEND_URL || '';

const SIZE_HEIGHT = {
  xs:  'h-6',
  sm:  'h-8',
  md:  'h-10',
  lg:  'h-14',
  xl:  'h-20',
  '2xl': 'h-28',
};

const initials = (bank, name) => {
  const src = (bank || name || '?').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/).filter(Boolean);
  return ((parts[0] || '')[0] + (parts[1] || '')[0]).toUpperCase() || '?';
};

const fallbackTone = (id) => {
  // Deterministic colour from card id so two cards in a list never collide.
  if (!id) return { bg: 'bg-muted', fg: 'text-foreground/80' };
  const hash = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = [
    { bg: 'bg-accent/15',  fg: 'text-accent'           },
    { bg: 'bg-violet-500/15', fg: 'text-violet-500'    },
    { bg: 'bg-amber-500/15',  fg: 'text-amber-600'     },
    { bg: 'bg-emerald-500/15', fg: 'text-emerald-600'  },
    { bg: 'bg-sky-500/15',     fg: 'text-sky-600'      },
    { bg: 'bg-rose-500/15',    fg: 'text-rose-600'     },
  ];
  return tones[hash % tones.length];
};

/**
 * Props:
 *   cardId    string  required
 *   bank      string  used by the fallback monogram
 *   name      string  used by the fallback monogram
 *   variant   'thumb' (default) | 'preview' | 'processed'
 *   size      'xs'..'2xl' (default 'md')
 *   bust      number  cache-buster appended to the URL as ?v=
 *   alt       string  defaults to "<bank> <name>"
 *   className extra tailwind classes for the wrapper
 *   asTile    bool    if true, wrap in a rounded card-like tile with shadow
 *   isVertical bool   render the tile in portrait aspect (1 / 1.586) instead
 *                     of landscape. Default false. Wire from
 *                     card.image.metadata.features.is_vertical for accurate
 *                     per-card layout.
 */
export const CardImage = ({
  cardId,
  bank,
  name,
  variant = 'thumb',
  size = 'md',
  bust,
  alt,
  className = '',
  asTile = false,
  hasImage = true,        // hint from the parent; we fail gracefully anyway
  isVertical = false,     // Phase 12.4 — portrait aspect for vertical cards
}) => {
  const heightClass = SIZE_HEIGHT[size] || SIZE_HEIGHT.md;
  const aspectClass = isVertical ? 'aspect-[1/1.586]' : 'aspect-[1.586/1]';
  const [errored, setErrored] = useState(false);
  const src = useMemo(() => {
    if (!cardId) return null;
    const b = bust ? `?v=${encodeURIComponent(bust)}` : '';
    return `${BASE}/api/assets/cards/${cardId}/${variant}${b}`;
  }, [cardId, variant, bust]);

  const showFallback = !src || !hasImage || errored;
  const tone = fallbackTone(cardId);
  const initialsStr = initials(bank, name);

  const inner = showFallback ? (
    <div
      className={cn(
        'grid place-items-center font-display font-semibold tracking-tight rounded-lg',
        tone.bg,
        tone.fg,
        heightClass,
        aspectClass,
      )}
      aria-label={alt || `${bank || ''} ${name || ''}`.trim()}
      data-testid={`card-image-fallback-${cardId}`}
    >
      <span className={cn(
        size === 'xs' ? 'text-[9px]' :
        size === 'sm' ? 'text-[10px]' :
        size === 'md' ? 'text-xs' :
        size === 'lg' ? 'text-sm' :
        size === 'xl' ? 'text-base' :
        'text-lg',
      )}>
        {initialsStr}
      </span>
    </div>
  ) : (
    <img
      src={src}
      alt={alt || `${bank || ''} ${name || ''}`.trim() || 'Card'}
      onError={() => setErrored(true)}
      className={cn(
        'object-contain block',
        heightClass,
        aspectClass,
      )}
      draggable="false"
      data-testid={`card-image-${cardId}`}
    />
  );

  if (!asTile) return <span className={cn('inline-block shrink-0', className)}>{inner}</span>;
  return (
    <span
      className={cn(
        'inline-block shrink-0 rounded-xl overflow-hidden',
        'ring-1 ring-border bg-[hsl(var(--surface-2))] shadow-sm',
        className,
      )}
    >
      {inner}
    </span>
  );
};

export default CardImage;
